import React, { useState, useEffect } from 'react';
import {
    Box,
    Title,
    Text,
    TextInput,
    Textarea,
    Button,
    Select,
    SimpleGrid,
    Paper,
    Alert,
    Switch,
    Table,
    Group,
    Badge,
} from '@mantine/core';
import { entityService } from '../../services/entityServiceSingleton';
import { ScimPersonService } from '../../services/ScimPersonService';
import { scimBuildingService } from '../../services/ScimLocalityService';
import { scimSchoolService } from '../../services/ScimLocalityService';
import { scimClassService } from '../../services/ScimLocalityService';
import { scimResponsibilityCenterService } from '../../services/ScimLocalityService';
import { debugLog, debugVerboseLog } from '../../utils/debugLog';

interface SpeedTestResult {
    url: string;
    times: number[];
    initialSpeed: number;
    averageSpeed: number;
    medianSpeed: number;
    minSpeed: number;
    maxSpeed: number;
    successCount: number;
    errorCount: number;
    errors: string[];
}

interface ComparisonResult {
    test1: SpeedTestResult;
    test2: SpeedTestResult;
    comparison: {
        averageDifference: number;
        averagePercentageDifference: number;
        fasterTest: 'test1' | 'test2' | 'tie';
    };
}

const SpeedComparison: React.FC = () => {
    const [selectedEntityType, setSelectedEntityType] = useState<string>('');
    const [url1, setUrl1] = useState<string>('');
    const [url2, setUrl2] = useState<string>('');
    const [numCalls, setNumCalls] = useState<number>(10);
    const [runInParallel, setRunInParallel] = useState<boolean>(true);
    const [addRestBetweenCalls, setAddRestBetweenCalls] = useState<boolean>(false);
    const [restDuration, setRestDuration] = useState<number>(1000);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [results, setResults] = useState<ComparisonResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<{ elapsed: number; estimated: number; percentage: number } | null>(null);
    const [startTime, setStartTime] = useState<number | null>(null);

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (isRunning && startTime) {
            interval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const estimated = calculateEstimatedDuration();
                const percentage = Math.min((elapsed / estimated) * 100, 100);

                setProgress({
                    elapsed,
                    estimated,
                    percentage
                });
            }, 2000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isRunning, startTime, numCalls, addRestBetweenCalls, restDuration, runInParallel]);

    const calculateEstimatedDuration = () => {
        const estimatedApiTime = 900;
        const singleTestTime = numCalls * estimatedApiTime + (addRestBetweenCalls ? (numCalls - 1) * restDuration : 0);

        if (runInParallel) {
            return singleTestTime;
        } else {
            return singleTestTime * 2;
        }
    };

    const estimatedDuration = calculateEstimatedDuration();

    const formatDuration = (ms: number) => {
        const seconds = Math.ceil(ms / 1000);
        if (seconds < 60) {
            return `${seconds}s`;
        } else {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes}m ${remainingSeconds}s`;
        }
    };

    const entityTypes = [
        'Person',
        'Employee',
        'Contractor',
        'Student',
        'Building',
        'School',
        'Class',
        'ResponsibilityCenter',
        'Group',
        'Vacancy'
    ];

    useEffect(() => {
        if (selectedEntityType) {
            const baseUrl = '/api/scim/v2';
            if (selectedEntityType === 'Person') {
                setUrl1(`${baseUrl}/entities/Person?skipReferenceExpansion=true`);
                setUrl2(`${baseUrl}/entities/Person?skipReferenceExpansion=false`);
            } else {
                setUrl1(`${baseUrl}/entities/${selectedEntityType}?skipReferenceExpansion=true`);
                setUrl2(`${baseUrl}/entities/${selectedEntityType}?skipReferenceExpansion=false`);
            }
        }
    }, [selectedEntityType]);

    const runSpeedTest = async (url: string, testName: string, addRest: boolean = false, restDuration: number = 1000): Promise<SpeedTestResult> => {
        const times: number[] = [];
        const errors: string[] = [];
        let successCount = 0;
        let errorCount = 0;

        debugLog(`🚀 Starting speed test for ${testName}: ${url}`);

        for (let i = 0; i < numCalls; i++) {
            const startTime = performance.now();

            try {
                let response;
                if (url.includes('/entities/Person')) {
                    const personService = new ScimPersonService();
                    response = await personService.searchPersons({
                        page: 1,
                        pageSize: 25
                    });
                } else if (url.includes('/entities/Building')) {
                    response = await scimBuildingService.searchBuildings({
                        page: 1,
                        pageSize: 25
                    });
                } else if (url.includes('/entities/School')) {
                    response = await scimSchoolService.searchSchools({
                        page: 1,
                        pageSize: 25
                    });
                } else if (url.includes('/entities/Class')) {
                    response = await scimClassService.searchClasses({
                        page: 1,
                        pageSize: 25
                    });
                } else if (url.includes('/entities/ResponsibilityCenter')) {
                    response = await scimResponsibilityCenterService.searchResponsibilityCenters({
                        page: 1,
                        pageSize: 25
                    });
                } else {
                    const entityType = selectedEntityType.toLowerCase();
                    response = await entityService.getEntitiesByType(entityType, 1, 25);
                }

                const endTime = performance.now();
                const duration = endTime - startTime;
                times.push(duration);
                successCount++;

                debugVerboseLog(`✅ ${testName} call ${i + 1}: ${duration.toFixed(2)}ms`);
            } catch (err) {
                const endTime = performance.now();
                const duration = endTime - startTime;
                times.push(duration);
                errorCount++;
                const errorMsg = err instanceof Error ? err.message : 'Unknown error';
                errors.push(`Call ${i + 1}: ${errorMsg}`);
                console.error(`❌ ${testName} call ${i + 1} failed:`, errorMsg);
            }

            if (addRest && i < numCalls - 1) {
                debugLog(`⏸️ Resting for ${restDuration}ms before next call...`);
                await new Promise(resolve => setTimeout(resolve, restDuration));
            }
        }

        const sortedTimes = [...times].sort((a, b) => a - b);
        const initialSpeed = times[0] || 0;
        const averageSpeed = times.reduce((sum, time) => sum + time, 0) / times.length;
        const medianSpeed = sortedTimes.length % 2 === 0
            ? (sortedTimes[sortedTimes.length / 2 - 1] + sortedTimes[sortedTimes.length / 2]) / 2
            : sortedTimes[Math.floor(sortedTimes.length / 2)];
        const minSpeed = Math.min(...times);
        const maxSpeed = Math.max(...times);

        return {
            url,
            times,
            initialSpeed,
            averageSpeed,
            medianSpeed,
            minSpeed,
            maxSpeed,
            successCount,
            errorCount,
            errors
        };
    };

    const runComparison = async () => {
        if (!url1 || !url2) {
            setError('Please provide both URLs');
            return;
        }

        setIsRunning(true);
        setError(null);
        setResults(null);
        setProgress(null);
        setStartTime(Date.now());

        try {
            let test1Result: SpeedTestResult;
            let test2Result: SpeedTestResult;

            if (runInParallel) {
                debugLog('🚀 Running tests in parallel...');
                [test1Result, test2Result] = await Promise.all([
                    runSpeedTest(url1, 'Test 1', addRestBetweenCalls, restDuration),
                    runSpeedTest(url2, 'Test 2', addRestBetweenCalls, restDuration)
                ]);
            } else {
                debugLog('🚀 Running tests in sequence...');
                test1Result = await runSpeedTest(url1, 'Test 1', addRestBetweenCalls, restDuration);
                test2Result = await runSpeedTest(url2, 'Test 2', addRestBetweenCalls, restDuration);
            }

            const averageDifference = test1Result.averageSpeed - test2Result.averageSpeed;
            const averagePercentageDifference = Math.abs(averageDifference) / Math.max(test1Result.averageSpeed, test2Result.averageSpeed) * 100;
            const fasterTest = averageDifference < 0 ? 'test1' : averageDifference > 0 ? 'test2' : 'tie';

            const comparison: ComparisonResult = {
                test1: test1Result,
                test2: test2Result,
                comparison: {
                    averageDifference,
                    averagePercentageDifference,
                    fasterTest
                }
            };

            setResults(comparison);
            debugLog('✅ Speed comparison completed:', comparison);
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(`Speed comparison failed: ${errorMsg}`);
            console.error('❌ Speed comparison failed:', err);
        } finally {
            setIsRunning(false);
            setProgress(null);
            setStartTime(null);
        }
    };

    const formatTime = (time: number): string => {
        return `${time.toFixed(2)}ms`;
    };

    const entityTypeSelectData = entityTypes.map((type) => ({
        value: type,
        label: type,
    }));

    return (
        <Box p="lg">
            <Title order={4} mb="xs">
                API Speed Comparison Utility
            </Title>

            <Text size="sm" c="dimmed" mb="lg">
                Compare the performance of different API endpoints with configurable parameters.
            </Text>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Paper withBorder p="md">
                    <Title order={6} mb="xs">
                        Configuration
                    </Title>

                    <Select
                        label="Entity Type"
                        data={entityTypeSelectData}
                        value={selectedEntityType}
                        onChange={(v) => setSelectedEntityType(v || '')}
                        mb="md"
                    />

                    <Textarea
                        label="Test 1 URL"
                        value={url1}
                        onChange={(e) => setUrl1(e.currentTarget.value)}
                        mb="md"
                        minRows={2}
                    />

                    <Textarea
                        label="Test 2 URL"
                        value={url2}
                        onChange={(e) => setUrl2(e.currentTarget.value)}
                        mb="md"
                        minRows={2}
                    />

                    <TextInput
                        label="Number of Calls"
                        type="number"
                        value={String(numCalls)}
                        onChange={(e) => setNumCalls(parseInt(e.currentTarget.value) || 10)}
                        min={1}
                        max={100}
                        mb="md"
                    />

                    <Switch
                        label="Run tests in parallel"
                        checked={runInParallel}
                        onChange={(e) => setRunInParallel(e.currentTarget.checked)}
                        mb="md"
                    />

                    <Switch
                        label="Add rest between calls"
                        checked={addRestBetweenCalls}
                        onChange={(e) => setAddRestBetweenCalls(e.currentTarget.checked)}
                        mb="md"
                    />

                    {addRestBetweenCalls && (
                        <TextInput
                            label="Rest Duration (ms)"
                            type="number"
                            value={String(restDuration)}
                            onChange={(e) => setRestDuration(parseInt(e.currentTarget.value) || 1000)}
                            min={100}
                            max={10000}
                            step={100}
                            description="Duration to wait between API calls (100-10000ms)"
                            mb="md"
                        />
                    )}

                    <Paper withBorder p="md" mb="md" bg="var(--mantine-color-default-hover)">
                        <Text size="sm" c="dimmed" mb="xs">
                            Estimated Duration:
                        </Text>
                        <Title order={6} c="blue" fw={700}>
                            {formatDuration(estimatedDuration)}
                        </Title>
                        <Text size="xs" c="dimmed">
                            {runInParallel
                                ? `Parallel: ${numCalls} calls × ~900ms per call${addRestBetweenCalls ? ` + ${numCalls - 1} rest periods × ${restDuration}ms` : ''}`
                                : `Sequential: ${numCalls} calls × 2 tests × ~900ms per call${addRestBetweenCalls ? ` + ${numCalls - 1} rest periods × ${restDuration}ms × 2` : ''}`
                            }
                        </Text>
                    </Paper>

                    {isRunning && progress && (
                        <Paper withBorder p="md" mb="md" bg="var(--mantine-color-default-hover)">
                            <Text size="sm" c="dimmed" mb="xs">
                                Estimated Progress:
                            </Text>
                            <Title order={6} fw={700}>
                                {Math.round(progress.percentage)}% Complete
                            </Title>
                            <Text size="xs" c="dimmed">
                                Elapsed: {formatDuration(progress.elapsed)} / Estimated: {formatDuration(progress.estimated)}
                            </Text>
                        </Paper>
                    )}

                    <Button
                        onClick={runComparison}
                        loading={isRunning}
                        disabled={!url1 || !url2}
                        fullWidth
                        size="lg"
                    >
                        Run Speed Comparison
                    </Button>
                </Paper>

                <Paper withBorder p="md">
                    <Title order={6} mb="xs">
                        Results
                    </Title>

                    {error && (
                        <Alert color="red" mb="md">
                            {error}
                        </Alert>
                    )}

                    {results && (
                        <Box>
                            <Box mb="lg">
                                <Title order={6} mb="xs">
                                    Summary
                                </Title>

                                <Group gap="md" mb="md">
                                    <Badge
                                        color={results.comparison.fasterTest === 'test1' ? 'green' : 'gray'}
                                        size="lg"
                                    >
                                        Test 1: {formatTime(results.test1.averageSpeed)}
                                    </Badge>
                                    <Badge
                                        color={results.comparison.fasterTest === 'test2' ? 'green' : 'gray'}
                                        size="lg"
                                    >
                                        Test 2: {formatTime(results.test2.averageSpeed)}
                                    </Badge>
                                </Group>

                                <Text size="sm" c="dimmed">
                                    {results.comparison.fasterTest === 'tie'
                                        ? 'Both tests performed equally'
                                        : `Test ${results.comparison.fasterTest === 'test1' ? '1' : '2'} is ${results.comparison.averagePercentageDifference.toFixed(1)}% faster`
                                    }
                                </Text>
                            </Box>

                            <Box mb="lg" pt="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }} />

                            <Paper withBorder mah={400} style={{ overflow: 'auto' }}>
                                <Table>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Metric</Table.Th>
                                            <Table.Th ta="right">Test 1</Table.Th>
                                            <Table.Th ta="right">Test 2</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        <Table.Tr>
                                            <Table.Td>Initial Speed</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test1.initialSpeed)}</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test2.initialSpeed)}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td>Average Speed</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test1.averageSpeed)}</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test2.averageSpeed)}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td>Median Speed</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test1.medianSpeed)}</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test2.medianSpeed)}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td>Min Speed</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test1.minSpeed)}</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test2.minSpeed)}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td>Max Speed</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test1.maxSpeed)}</Table.Td>
                                            <Table.Td ta="right">{formatTime(results.test2.maxSpeed)}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td>Success Count</Table.Td>
                                            <Table.Td ta="right">{results.test1.successCount}</Table.Td>
                                            <Table.Td ta="right">{results.test2.successCount}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td>Error Count</Table.Td>
                                            <Table.Td ta="right">{results.test1.errorCount}</Table.Td>
                                            <Table.Td ta="right">{results.test2.errorCount}</Table.Td>
                                        </Table.Tr>
                                    </Table.Tbody>
                                </Table>
                            </Paper>

                            {(results.test1.errors.length > 0 || results.test2.errors.length > 0) && (
                                <Box mt="md">
                                    <Title order={6} mb="xs">
                                        Errors
                                    </Title>
                                    {results.test1.errors.length > 0 && (
                                        <Box mb="md">
                                            <Text size="sm" fw={600} mb="xs">Test 1 Errors:</Text>
                                            {results.test1.errors.map((error, index) => (
                                                <Text key={index} size="sm" c="red">
                                                    {error}
                                                </Text>
                                            ))}
                                        </Box>
                                    )}
                                    {results.test2.errors.length > 0 && (
                                        <Box>
                                            <Text size="sm" fw={600} mb="xs">Test 2 Errors:</Text>
                                            {results.test2.errors.map((error, index) => (
                                                <Text key={index} size="sm" c="red">
                                                    {error}
                                                </Text>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>
            </SimpleGrid>
        </Box>
    );
};

export default SpeedComparison;
